import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { apiGet, apiSend } from "@/lib/api-client";
import { logger } from "@/lib/logger";
import { configKeys } from "@/lib/query";
import { type ParamWorkflow, paramWorkflowSchema } from "@/schemas/config";

type WorkflowEdits = Partial<Pick<ParamWorkflow, "claimTeamHistoryWorkflowTo" | "vinylUrl">>;

export function ConfigWorkflowsPage() {
  const queryClient = useQueryClient();
  // Edits overlay rather than a synced copy: form values are derived
  // (edits.field ?? param.field) on every render, so there's no transient
  // state where the query has resolved but the form hasn't synced — avoids
  // the post-load skeleton flicker.
  const [edits, setEdits] = useState<WorkflowEdits>({});

  const {
    data: param,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: configKeys.paramWorkflow,
    queryFn: () => apiGet("/api/config/param/workflow", paramWorkflowSchema),
  });

  const saveMutation = useMutation({
    mutationFn: (data: { claimTeamHistoryWorkflowTo: string; vinylUrl: string }) => {
      if (!param) throw new Error("Param row not loaded");
      return apiSend("PUT", `/api/config/param/${param.id}`, data, z.unknown());
    },
    onSuccess: () => {
      toast.success("Workflow configuration saved");
      setEdits({});
      // Both Workflows and Upload PDFs project the same singleton Param row,
      // so invalidate the shared parent key — keeps the two views in sync.
      queryClient.invalidateQueries({ queryKey: configKeys.paramAll });
    },
    onError: (error) => {
      logger.error("workflows_save_failed", { error });
      toast.error(error instanceof Error ? error.message : "Failed to save workflow configuration");
    },
  });

  if (isLoading) {
    return (
      <div>
        <h1 className="font-display text-3xl font-bold">Workflows</h1>
        <div className="mt-4 space-y-3">
          <div className="h-10 animate-pulse rounded bg-muted" />
          <div className="h-10 animate-pulse rounded bg-muted" />
          <div className="h-9 w-24 animate-pulse rounded bg-muted" />
        </div>
      </div>
    );
  }

  if (isError) {
    logger.error("Failed to load workflow configuration", { error });
    return (
      <div>
        <h1 className="font-display text-3xl font-bold">Workflows</h1>
        <ErrorState message="Couldn't load workflow configuration." onRetry={() => refetch()} />
      </div>
    );
  }

  if (!param) return null;

  const form = {
    claimTeamHistoryWorkflowTo:
      edits.claimTeamHistoryWorkflowTo ?? param.claimTeamHistoryWorkflowTo,
    vinylUrl: edits.vinylUrl ?? param.vinylUrl,
  };
  const isDirty =
    form.claimTeamHistoryWorkflowTo !== param.claimTeamHistoryWorkflowTo ||
    form.vinylUrl !== param.vinylUrl;

  function handleSave() {
    if (!isDirty) {
      toast.info("No changes to save");
      return;
    }
    saveMutation.mutate(form);
  }

  return (
    <div>
      <div className="flex items-center justify-between rounded-md border bg-card p-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Workflows</h1>
          <p className="mt-1 text-muted-foreground">
            App-wide workflow targets and the Vinyl admin URL.
          </p>
        </div>
        <Button onClick={handleSave} disabled={saveMutation.isPending}>
          <Save className="size-4" />
          {saveMutation.isPending ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <Card className="mt-6">
        <CardHeader />
        <CardContent>
          <div className="grid gap-6">
            <div className="grid gap-1.5">
              <label className="font-medium text-sm" htmlFor="claim-team-history-workflow-to">
                Claim Team History Workflow Recipient
              </label>
              <p className="text-muted-foreground text-xs">
                Email address that receives the workflow notification when claim team history is
                updated.
              </p>
              <Input
                id="claim-team-history-workflow-to"
                value={form.claimTeamHistoryWorkflowTo}
                onChange={(e) => setEdits({ ...edits, claimTeamHistoryWorkflowTo: e.target.value })}
                placeholder="recipient@memic.com"
              />
            </div>
            <div className="grid gap-1.5">
              <label className="font-medium text-sm" htmlFor="vinyl-url">
                Vinyl URL
              </label>
              <p className="text-muted-foreground text-xs">
                Root URL for the legacy Vinyl admin tooling.
              </p>
              <Input
                id="vinyl-url"
                value={form.vinylUrl}
                onChange={(e) => setEdits({ ...edits, vinylUrl: e.target.value })}
                placeholder="https://vinyl.memic.com"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
